"use strict";

import Common           from "./Common";
import Sprite3D         from "./Sprite3D";
import Betty            from "./Betty";
import Santa            from "./Santa";
import levels           from "./Levels";
import PeekingBetty     from "./PeekingBetty";
import BettyController  from "./BettyController";
import PausePanel       from "./PausePanel";
import ScoreManager     from "./ScoreManager";
import GlitchFilter     from "./GlitchFilter";
import Simon            from "./Simon";
import Shake            from "./Shake";
import TutorialPopup    from "./TutorialPopup"
import isMobile         from "ismobilejs";

/**
 * @class GameScene @extends P3.Scene
 */
export default class GameScene extends P3.Scene {
    constructor() {
        super();

        this.interactive = true;
        this.cursor = "url(./assets/images/icon_target.png) 25 25, default";

        /**
         * @type {*|Container2d}
         * @private
         */
        this._container3D = new PIXI.projection.Container2d();

        /**
         * @type {PIXI.Container}
         * @private
         */
        this._projectionHolder = new PIXI.Container();

        /**
         * @type {PIXI.Container}
         * @private
         */
        this._bgHolder = new PIXI.Container();

        /**
         * @type {PIXI.Container}
         * @private
         */
        this._worldHolder = Common.world = new PIXI.Container();

        /**
         * @type {PIXI.Container}
         * @private
         */
        this._uiHolder = Common.uiLayer = new PIXI.Container();

        /**
         * @type {Array}
         * @private
         */
        this._pathBetties = [];

        /**
         * @type {Array}
         * @private
         */
        this._peekBetties = [];

        /**
         * @type {BettyController}
         * @private
         */
        this._bettyController = null;

        /**
         * @type {Array}
         * @private
         */
        this._paths = [];

        /**
         * @type {e.BlurFilter}
         * @private
         */
        this._blurFilter = new PIXI.filters.BlurFilter(2, 1, 1);

        /**
         * @type {PixelGlitchFilter}
         * @private
         */
        this._glitchFilter = new GlitchFilter(10);
        this._glitchFilter.uAmp = 0.001;
        this._glitchFilter.uFreq = 0.002;

        /**
         * @type {PIXI.projection.Sprite2d}
         * @private
         */
        this._projectionPlane = null;

        /**
         * @type {Button}
         * @private
         */
        this._pauseBtn = null;

        /**
         * @type {Button}
         * @private
         */
        this._soundBtn = null;

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._scorePanel = null;

        /**
         * @type {PausePanel}
         * @private
         */
        this._pausePanel = null;

        /**
         * @type {null}
         * @private
         */
        this._scoreManager = null;

        /**
         * @type {Array}
         * @private
         */
        this._particles = [];

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._meter = null;

        /**
         * @type {Array}
         * @private
         */
        this._testPath = null;

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._overlayL = null;

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._overlayR = null;

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._bulletPanel = null;

        /**
         * @type {PIXI.Sprite}
         * @private
         */
        this._bg = null;

        /**
         * @type {null}
         * @private
         */
        this._santa = null;

        /**
         * @type {boolean}
         * @private
         */
        this._inGrace = true;

        /**
         * @type {number}
         * @private
         */
        this._sendSantaTime = 0;

        /**
         * @type {number}
         * @private
         */
        this._sendSantaDelay = 20;

        /**
         * @type {Shake}
         * @private
         */
        this._cameraShake = Common.cameraShake = new Shake();

        /**
         * @type {number}
         * @private
         */
        this._tutorialState = -1;

        /**
         * @type {TutorialPopup}
         * @private
         */
        this._tutorialPopup = new TutorialPopup();
    }

    /**
     * @public
     */
    init() {
        Common.gameScene = this;
        Common.renderer.backgroundColor = 0xfefcef;

        // this._tutorialPopup.visible = false;
        this._tutorialPopup.x = Common.STAGE_WIDTH * 0.5;
        this._tutorialPopup.y = Common.STAGE_HEIGHT * 0.5;
        this._uiHolder.addChild(this._tutorialPopup);

        this._worldHolder.x = Common.STAGE_WIDTH * 0.5;
        this._worldHolder.y = Common.STAGE_HEIGHT * 0.5;
        this._cameraShake.setTarget(this._worldHolder);
        this._cameraShake.setRotCentre({x: Common.STAGE_WIDTH * 0.5, y: Common.STAGE_HEIGHT * 0.5});
        this._cameraShake.useRotation = true;

        this._cameraShake.maxOffsetX = 10;
        this._cameraShake.maxOffsetY = 10;
        this._cameraShake.maxRot = Math.PI * 0.01;

        //--load game at start (actually in this game this just ensures tutorial doesn't show if played has played previously--//
        Common.loadGame();

        //--reset results--//
        Common.results = {};

        //--add holders to scene--//
        this._worldHolder.addChild(this._bgHolder, this._projectionHolder);
        this.addChild(this._worldHolder, this._uiHolder);

        //--create bg--//
        this._bg = P3.createSprite("gameplay_arena_bg", this._bgHolder);
        this._bg.x = P3.View.width * 0.5;
        this._bg.interactive = true;
        this._bg.mousedown = this._bg.touchstart = this._missShot.bind(this);

        //--loads path from json and converts to an array of points--//
        this._loadAndConvertPaths();

        //--init ui and score manager and pass in our score panel--//
        this._initUI();
        const cb = {
            onEmpty: ()=> {
                this._onBulletsEmpty();
            },
            onReload: ()=> {
                this._onBulletsReload();
            }
        };
        this._scoreManager = Common.scoreManager = new ScoreManager(this._scorePanel, this._bulletPanel, this._meter, this.gameOver.bind(this), cb);

        //--more init--//
        this._initProjectionView();
        this._initLevel();
        this._initBettyController();
        this._initBetties();
        //this._initSanta();
        this.initSimon();

        //--add screen overlays--//
        this._overlayL = P3.createSprite("fg_wall_left", this._worldHolder);
        this._overlayL.anchor.set(0, 0);

        this._overlayR = P3.createSprite("fg_wall_right", this._worldHolder);
        this._overlayR.anchor.set(1, 0);

        //--add keyboard events--//
        let reload = Common.keyboard(32);
        reload.up = ()=> {
            //console.log("key up");
            this._scoreManager.refillBullets();
        };

        //--start the game--//
        this.start();


    }

    initSimon() {
        const simon = new Simon(this._container3D);
        this._worldHolder.addChild(simon.view);
        this._simon = simon;
    }

    /**
     * @public
     */
    start() {

        const oldPos = new PIXI.Point(this._meter.panel.x, this._meter.panel.y);
        this._meter.panel.y += 150;

        TweenMax.to(this._meter.panel, 0.3, {y: oldPos.y, ease: Power1.easeOut, delay: 0.5});

        const oldPos2 = new PIXI.Point(this._scorePanel.x, this._scorePanel.y);
        this._scorePanel.y -= 150;
        this._scorePanel.back1.y -= 150;
        this._scorePanel.back2.y -= 150;

        TweenMax.to(this._scorePanel, 0.3, {y: oldPos2.y, delay: 0.8, ease: Back.easeOut});
        TweenMax.to(this._scorePanel.back1, 0.3, {y: oldPos2.y, delay: 0.8, ease: Power1.easeOut});
        TweenMax.to(this._scorePanel.back2, 0.3, {y: oldPos2.y, delay: 0.8, ease: Power1.easeOut});

        Common.audio.stopMusic();
        Common.audio.playMusic("music_gameplay_loop_00");
        this.afx = Common.audio.playSound("sfx_ambient_loop_00", {loop: true});

        // Common.isFirstPlay = true;
        if (!Common.isFirstPlay) {
            delay(()=> {//start game running after a pause
                this._running = true;
                this._inGrace = false;
            }, 2, this);
        } else {
            this._pauseBtn.visible = false;
            this._inGrace = false;
            this._onUnpauseStartTutorial = true;
            this._pauseButtonDown();
            // delay(()=>this.tutorial(), 1);

        }

    }

    tutorial() {

        this._tutorialState++;
        console.log("tutorial state : " + this._tutorialState);

        switch (this._tutorialState) {
            case 0:

                this._tutorialPopup.show(Common.copy.tut_popup_1[Common.language]);

                //  delay(()=>this._tutorialPopup.hide(), 2);

                //remove some bullets
                for (let i = 0; i < 6; i++) {
                    this._scoreManager.useBullet();
                }
                delay(()=> {
                    this._tutorialPopup.hide();
                    this._inTut = true;
                    this.tutBetty = this._createBetty(false);
                    this.tutBetty.tutorialBetty = true;
                    this.tutBetty.immortal = true;
                    this.tutBetty.path = this._paths[6].slice(0);//copy
                    this.tutBetty.wakeUp();

                    this.tutBetty.tutCB = ()=> this.tutorialStateZeroCB();

                    delay(()=> {
                        this._inTut = false;

                        let str = "cursor";
                        if (isMobile.any) str = "finger";

                        this._finger = P3.createSprite("tutorial_" + str, Common.world);
                        this._finger.anchor.set(0.5);

                        const lhs = (Common.STAGE_WIDTH - P3.View.width) * 0.5;
                        let global = {x: lhs + this.tutBetty.worldTransform.tx, y: this.tutBetty.worldTransform.ty};

                        this._finger.x = global.x;
                        this._finger.y = global.y - 75;


                        TweenMax.to(this._finger.scale, 0.5, {
                            yoyo: true,
                            repeat: -1,
                            x: 0.7,
                            y: 0.7,
                            ease: Power1.easeIn,
                            yoyoEase: Power1.easeOut
                        });

                        this.tutBetty.immortal = false;//make betty killable again


                    }, 2.75, this);
                    this._peekBetties[0].onTutProjectileDown = () => {

                        this._tutorialPopup.show(Common.copy.tut_popup_3[Common.language]);

                        delay(()=>this._tutorialPopup.hide(), 2.5);

                        this.projectileDownComplete = true;

                        this._uiHolder.addChild(this._finger);//move to ui container
                        this._finger.visible = true;
                        this._finger.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 + 125;
                        this._finger.y = P3.View.height * 0.5 + 175;

                        this._scoreManager.onRefillTutComplete = ()=> {
                            TweenMax.killTweensOf(this._finger);

                            this._finger.visible = false;
                            this.tutorial();

                        };
                    };

                }, 2);
                break;
            case 1:
                delay(()=> {

                    this._tutorialPopup.show(Common.copy.tut_popup_4[Common.language]);

                    this._inTut = true;
                    this.tutBetty = this._createBetty(true);
                    this.tutBetty.tutorialBetty = true;
                    this.tutBetty.immortal = true;
                    this.tutBetty.path = this._paths[6].slice(0);//copy
                    this.tutBetty.wakeUp();

                    this.tutBetty.tutCB = ()=> {

                        this._inTut = true;
                        this._finger.visible = false;

                        this.tutorial();
                    };

                    delay(()=> {
                        this._tutorialPopup.hide();
                        this._inTut = false;

                        //   let global = this.tutBetty.view.toGlobal(Common.world);


                        const lhs = (Common.STAGE_WIDTH - P3.View.width) * 0.5;
                        let global = {x: lhs + this.tutBetty.worldTransform.tx, y: this.tutBetty.worldTransform.ty};
                        this._finger.x = global.x;
                        this._finger.y = global.y - 75;

                        TweenMax.to(this._finger.scale, 0.5, {
                            yoyo: true,
                            repeat: -1,
                            x: 0.7,
                            y: 0.7,
                            ease: Power1.easeIn,
                            yoyoEase: Power1.easeOut
                        });

                        this.tutBetty.immortal = false;//make betty killable again

                    }, 2.35, this);

                    delay(()=> {
                        this._inTut = false;

                        let str = "cursor";
                        if (isMobile.any) str = "finger";

                        this._finger = P3.createSprite("tutorial_" + str, Common.world);
                        this._finger.anchor.set(0.5);

                        const lhs = (Common.STAGE_WIDTH - P3.View.width) * 0.5;
                        let global = {x: lhs + this.tutBetty.worldTransform.tx, y: this.tutBetty.worldTransform.ty};
                        this._finger.x = global.x;
                        this._finger.y = global.y - 75;

                        TweenMax.to(this._finger.scale, 0.5, {
                            yoyo: true,
                            repeat: -1,
                            x: 0.7,
                            y: 0.7,
                            ease: Power1.easeIn,
                            yoyoEase: Power1.easeOut
                        });

                        this.tutBetty.immortal = false;//make betty killable again

                    }, 2.35, this);


                }, 2);

                break;
            case 2:

                this._tutorialPopup.show(Common.copy.tut_popup_5[Common.language]);

                //make finger visible after a short delay

                var myPresent;
                //send simon to popout and drop a present
                this._simon.popout((present)=> {
                    present.skipDecay = true;
                    myPresent = present;
                    present.tutCB = ()=> {
                        this._finger.visible = false;

                        //reactivate game at end of tutorial
                        this._finger.destroy();

                        this._running = true;
                        this._inGrace = false;
                        this._inTut = false;

                        this._scoreManager.refillBullets();

                        this._pauseBtn.visible = true;

                        Common.buttonsAppear([this._pauseBtn]);

                    };
                });

                delay(()=> {
                    this._finger.visible = true;
                    this._tutorialPopup.hide();

                    const lhs = (Common.STAGE_WIDTH - P3.View.width) * 0.5;
                    let global = {x: lhs + myPresent.worldTransform.tx, y: myPresent.worldTransform.ty};
                    this._finger.x = global.x;
                    this._finger.y = global.y + 35;
                }, 3.5);

                break;
            default:
                console.warn("unknown tutorial state : " + this._tutorialState);
                break;
        }

    }

    tutorialStateZeroCB() {

        this._tutorialPopup.show(Common.copy.tut_popup_2[Common.language]);

        this._inTut = true;
        this._finger.visible = false;

        delay(()=> {

            this._tutorialPopup.hide();

            this._peekBetties[0].peek(true);//peek with fire true to gurantee betty shoots at us for tutorial

            delay(()=> {

                if (this.projectileDownComplete) return; //skip if player moused down on projectile during the delay period

                let projectile = this._peekBetties[0].lastFired;
                TweenMax.killTweensOf(projectile);
                TweenMax.killTweensOf(projectile.view);

                this._finger.visible = true;

                let global = this.tutBetty.view.toGlobal(Common.world);
                this._finger.x = Common.STAGE_WIDTH * 0.5;
                this._finger.y = global.y - 75;

                const pos = projectile.view.toGlobal(Common.world);
                this._finger.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 + pos.x - Common.STAGE_WIDTH * 0.5;
                this._finger.y = pos.y - Common.STAGE_HEIGHT * 0.5;

            }, 2.75, this);
        }, 2.5, this);

    }

    /**
     * @public
     */
    appear() {
        Common.buttonsAppear([this._soundBtn, this._pauseBtn]);
    }

    /**
     * @public
     */
    end() {
        this._uiHolder.visible = false;

        this._running = false;
        this.signals.next.dispatch(this);

        Common.audio.stopMusic();
        this.afx.stop();
    }

    /**
     * @public
     */
    destroy() {
        TweenMax.killAll();
        super.destroy();
        Common.audio.stopMusic();
    }

    /**
     * @public
     */
    resize() {
        this.x = (P3.View.width - Common.STAGE_WIDTH) * 0.5;

        this._soundBtn.x = (Common.STAGE_WIDTH + P3.View.width) * 0.5 - 100;
        this._pauseBtn.x = (Common.STAGE_WIDTH + P3.View.width) * 0.5 - 185;
        this._homeBtn.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 + 75;

        this._scorePanel.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 + 325;
        this._scorePanel.back1.x = this._scorePanel.back2.x = this._scorePanel.x;

        this._projectionPlane.width = P3.View.width;
        this._projectionPlane.x = Common.STAGE_WIDTH * 0.5;
        this._projectionHolder.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5;

        this._overlayL.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 - 150;
        this._overlayR.x = (Common.STAGE_WIDTH + P3.View.width) * 0.5 + 150;

        this._scoreManager.resize();

        const arr = this._container3D.children;
        for (let i = 0; i < arr.length; i++) {
            roundToTileGrid(arr[i]);
        }
    }

    /**
     * @public
     */
    gameOver() {

        if (Common.isDebug) return;

        //--play game over SFX--//
        Common.audio.playSound("sfx_gameover");

        //--top animations and logic--//
        TweenMax.pauseAll();
        this._running = false;
        //--resume health tweens--//
        this._scoreManager.healthTweens[0].resume();
        this._scoreManager.healthTweens[1].resume();

        //--handle scoring / save--//
        Common.results.score = this._scoreManager.score;
        Common.results.highestMulti = this._scoreManager.highestMultiplier;
        if (Common.results.score > Common.highestScore) {
            Common.highestScore = Common.results.score;
            Common.saveGame();
        }

        const pixelFilter = new PIXI.filters.PixelateFilter(15);

        //--set glitch filter active world filters--//
        this._worldHolder.filters = [pixelFilter];

        //--lazily define a recursive function to make the background glitch repeating--//
        var glitcher = ()=> {

            this._worldHolder.filters = [this._glitchFilter];

            delay(()=> {

                this._worldHolder.filters = [pixelFilter];

                delay(()=> {
                    glitcher();
                }, 0.5 + Math.random(), this);

            }, 0.15, this);

        };

        //--start glitcher recursive function running--//
        glitcher();

        //--end scene after a delay--//
        delay(this.end, 2.5, this);

        //--game ending flag--//
        this._gameend = true;

    }

    update() {

        //--if we are mobile and if we are not portrait then pause the game--
        if (isMobile.any && Common.view.orientation !== "landscape") {
            if (!this.isPause) {
                this._pauseButtonDown();
            }
        }

        this._cameraShake.update(deltaTime);

        //--update pixi projection--//
        this._updateProjection();

        //--run active game logic--//
        if (this._running) {
            this._bettyController.update();
            this._updateBetties();
            this._updateParticles();
            ///this._santa.update();
            this._scoreManager.update();

            //--drop present timer--//
            this._sendSantaTime += deltaTime;
            if (this._sendSantaTime > this._sendSantaDelay) {
                this._sendSantaTime = 0;
                this._sendSantaDelay = 40;
                //this._sendSanta();
                this._simon.popout();
            }
        } else if (this._inTut) {
            this.tutBetty.update();
        }

        //--depth sort--//
        P3.depthSortY(this._container3D);
        //add floor back at lowest level
        this._container3D.addChildAt(this._projectionPlane, 0);

    }

    /**
     * @private
     */
    _updateProjection() {
        //---projection code from PIXI examples---//
        this._container3D.proj.clear();
        this._container3D.updateTransform();

        // now we can get local coords for points of perspective
        let pos = this._container3D.toLocal({x: P3.View.width * 0.5, y: 200});//the y value here can be used to adjust projection depth

        //need to invert this thing, otherwise we'll have to use scale.y=-1 which is not good
        pos.x = -pos.x;
        pos.y = -pos.y;
        this._container3D.proj.setAxisY(pos, /*-squareFar.factor*/ -1);
    }

    /**
     * @private
     */
    _updateBetties() {
        let len;

        len = this._pathBetties.length;
        for (let i = 0; i < len; i++) {
            this._pathBetties[i].update();
        }
        len = this._peekBetties.length;
        for (let i = 0; i < len; i++) {
            this._peekBetties[i].update();
        }
    }

    /**
     * @private
     */
    _updateParticles() {
        let len;

        len = this._particles.length;
        for (let i = 0; i < len; i++) {
            this._particles[i].update(P3.deltaTime * 0.5);
        }

        len = Common.hearts.length;
        for (let i = 0; i < len; i++) {
            if (Common.hearts[i] && Common.hearts[i].update) {
                Common.hearts[i].update(P3.deltaTime * 0.5);
            }
        }

    }

    /**
     * @private
     */
    _initBetties() {

        const numEvil = 100;
        for (let i = 0; i < numEvil; i++) {
            let betty = this._createBetty();
            this._pathBetties.push(betty);
        }

        const numReal = 15;
        for (let i = 0; i < numReal; i++) {
            let betty = this._createBetty(true);
            this._pathBetties.push(betty);
        }

        this._pathBetties = Common.shuffleArr(this._pathBetties);
    }

    /**
     * @deprecated
     * @private
     */
    _initSanta() {
        this._santa = new Santa(this._container3D);
    }

    /**
     * @deprecated
     * @private
     */
    _sendSanta() {

        if (!this._santa.sleep) {
            console.warn("santa is already awake");
            return;
        }

        let p = this._paths[6].slice(0);//copy
        p[0].x += 800;//temp solution really, so santa finishes his path off screen
        this._santa.view.y = -300;
        this._santa.path = p;
        this._santa.sleep = false;
        this._santa.setTarget();
        this._santa.jumpToTarget();
        this._santa.view.visible = true;

        //-inc max pathing to increase difficulty--//
        this._bettyController.incMaxPathing();
    }

    /**
     * @private
     */
    _initProjectionView() {
        this._container3D = new PIXI.projection.Container2d();
        this._container3D.position.set(0, P3.View.height - 125);

        const plane = new PIXI.projection.Sprite2d(Common.assets.texture("floor_tile"));
        plane.anchor.set(0.5, 1);
        plane.x = P3.View.width * 0.5;
        plane.width = P3.View.width;
        plane.height = P3.View.height * 4;
        plane.floor = true;
        this._projectionPlane = plane;

        this._container3D.addChild(plane);
        this._projectionHolder.addChild(this._container3D);
    }

    /**
     * Creates real and evil betti's dependent on isReal flag
     * @param {boolean} isReal
     * @returns {Betty}
     * @private
     */
    _createBetty(isReal) {

        const cb = {
            onHit: (pos)=> {
                this._createHit(pos)
            }
        };

        let betty;
        if (isReal) {
            betty = new Betty("betty_good", this._container3D, this._scoreManager, cb);
            betty.isReal = true;
        } else {
            betty = new Betty("betty_bad", this._container3D, this._scoreManager, cb);
            betty.initParticles();
        }

        betty.setTint();
        betty.init();

        return betty;
    }

    /**
     * @private
     */
    _initLevel() {
        //todo - generalise this function better / sort this mess out
        let lvlData = levels["level_1"].map.slice(0);//take a copy
        lvlData = lvlData.reverse();//then reverse

        let sp;

        for (let y = 0; y < lvlData.length; y++) {
            for (let x = 0; x < lvlData[y].length; x++) {
                if (lvlData[y][x] === 1) {

                    let rand = P3.math.randomInt(0, 2);
                    sp = new Sprite3D("wall_tile_end_0" + rand);

                    sp.x = x * P3.View.width * 0.2 + 150;
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it
                    if (sp.x < P3.View.width * 0.5) {
                        sp.view.scale.x = -sp.view.scale.x;
                    }
                    if (x === lvlData[y].length - 1) {
                        sp.x -= 35;
                    }

                    sp.hitArea = new PIXI.Rectangle(0, 0, sp.width, sp.height * 0.5);

                    roundToTileGrid(sp);

                } else if (lvlData[y][x] === 2) {
                    sp = new Sprite3D("wall_tile_0" + P3.math.randomInt(1, 3));
                    sp.x = x * P3.View.width * 0.2 + 150;
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it

                    roundToTileGrid(sp);

                } else if (lvlData[y][x] === 3) {
                    sp = new Sprite3D("plant_0" + P3.math.randomInt(0, 3));
                    sp.x = x * P3.View.width * 0.2 + 150 + 25;
                    sp.y = y * (-P3.View.height * 0.2);
                    if (sp.x < P3.View.width * 0.25) {
                        sp.x += P3.math.randomInt(-300, -150);
                    } else if (sp.x > P3.View.width * 0.75) {
                        sp.x += P3.math.randomInt(300, 150);
                    }
                    sp.y += P3.math.randomInt(150, 300);

                    if (Math.random() < 0.5) {
                        sp.view.scale.x = -sp.view.scale.x;
                    }

                } else if (lvlData[y][x] === 4) {
                    sp = new Sprite3D("wall_1x1");
                    sp.x = x * P3.View.width * 0.2 + 150;
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it
                } else if (lvlData[y][x] === 6) {
                    sp = new Sprite3D("wall_tile_end_0" + P3.math.randomInt(1, 2));

                    sp.x = x * P3.View.width * 0.2 + 150;
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it

                    roundToTileGrid(sp);

                    let betty;
                    if (Math.random() < 0.75) {
                        betty = new PeekingBetty("betty_bad", this._container3D, this._scoreManager);
                    } else {
                        betty = new PeekingBetty("betty_good", this._container3D, this._scoreManager);
                        betty.isReal = true;
                    }
                    betty.currentState = "up";
                    betty.x = sp.x;
                    betty.y = sp.y - 5;
                    betty.view.y -= 40;

                    sp.hitArea = new PIXI.Rectangle(0, 0, sp.width, sp.height * 0.5);

                    this._peekBetties.push(betty);
                    this._container3D.addChild(betty);

                } else if (lvlData[y][x] === 7) {
                    sp = new Sprite3D("wall_tile_02");
                    sp.x = x * P3.View.width * 0.2 + 150;
                    sp.y = y * (-P3.View.height * 0.2);
                    // sp.interactive = true;//so it blocks interactions behind it

                    roundToTileGrid(sp);

                } else {
                    continue;
                }
                this._container3D.addChildAt(sp, 1);
            }
        }

        let edgeData = levels["level_1"].edgeL.slice(0);//copy
        edgeData = edgeData.reverse();//reverse
        this._createEdge(edgeData, -1);

        edgeData = levels["level_1"].edgeR.slice(0);//copy
        edgeData = edgeData.reverse();//reverse
        this._createEdge(edgeData, 1);

    }

    _createEdge(edgeData, dir) {
        let sp;
        for (let y = 0; y < edgeData.length; y++) {
            for (let x = 0; x < edgeData[y].length; x++) {
                if (edgeData[y][x] === 1) {
                    let rand = P3.math.randomInt(1, 2);
                    sp = new Sprite3D("wall_tile_end_0" + rand);
                    sp.view.scale.x = dir;
                    sp.x = x * (300 * dir);
                    sp.x += 150 * dir;
                    if (dir === 1) {
                        sp.x += P3.View.width;
                    }
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it
                    sp.mousedown = sp.touchstart = (e)=> this._missShot(e); //and uses a bullet
                    sp.hitArea = new PIXI.Rectangle(0, 0, sp.width, sp.height * 0.5);
                    roundToTileGrid(sp);
                }
                else if (edgeData[y][x] === 2) {
                    sp = new Sprite3D("wall_tile_0" + P3.math.randomInt(1, 3));
                    sp.view.scale.x = dir;
                    sp.x = x * (300 * dir);
                    sp.x += 150 * dir;
                    if (dir === 1) sp.x += P3.View.width;
                    sp.y = y * (-P3.View.height * 0.2);
                    sp.interactive = true;//so it blocks interactions behind it
                    sp.mousedown = sp.touchstart = (e)=> this._missShot(e); //and uses a bullet
                    roundToTileGrid(sp);
                } else if (edgeData[y][x] === 3) {
                    if (Math.random() < 0.75) {
                        sp = new PeekingBetty("betty_bad", this._container3D, this._scoreManager);
                    } else {
                        sp = new PeekingBetty("betty_good", this._container3D, this._scoreManager);
                        sp.isReal = true;
                    }
                    sp.currentState = "right";
                    sp.x = -150;
                    if (dir === 1) {
                        sp.currentState = "left";
                        sp.x = P3.View.width + 150;
                    }
                    if (Math.random() < 0.5) {
                        sp.currentState = "up";
                    }
                    roundToTileGrid(sp);
                    this._peekBetties.push(sp);
                } else {
                    continue;
                }

                sp.y = y * (-P3.View.height / 5);
                this._container3D.addChildAt(sp, 1);
            }
        }
    }

    /**
     * @private
     */
    _loadAndConvertPaths() {
        const numPaths = Common.config.total_paths;
        for (let i = 0; i < numPaths; i++) {
            let pathData = Common.assets.json("path_" + (i + 1));
            pathData = this._convertPath(pathData);
            pathData = pathData.reverse();
            this._paths.push(pathData);
        }

        if (Common.isDebug) {
            let pathData = Common.assets.json("path_test");
            pathData = this._convertPath(pathData);
            pathData = pathData.reverse();
            this._testPath = pathData;
        }
    }

    /**
     * @param pathData
     * @returns {Array}
     * @private
     */
    _convertPath(pathData) {
        const arr = [];
        let obj;
        for (let i = 0; i < pathData.length; i++) {
            if (i === 0) {
                obj = {
                    x: pathData[i].rectA.X / 200 * P3.View.width - 250,//here 250 is a magic number, not sure why its needed
                    y: pathData[i].rectA.Y / 800 * (-P3.View.height * 4)
                };
                arr.push(obj);
                obj = {
                    x: pathData[i].rectB.X / 200 * P3.View.width - 250,
                    y: pathData[i].rectB.Y / 800 * (-P3.View.height * 4)
                };
                arr.push(obj);
            } else {
                obj = {
                    x: pathData[i].rectB.X / 200 * P3.View.width - 250,
                    y: pathData[i].rectB.Y / 800 * (-P3.View.height * 4)
                };
                arr.push(obj);
            }
            roundToTileGrid(obj);
        }
        return arr;
    }

    /**
     * @private
     */
    _initBettyController() {
        this._bettyController = new BettyController(this._peekBetties, this._pathBetties, this._paths);
        if (Common.isDebug) this._bettyController.testPath = this._testPath;
    }

    /**
     * @private
     */
    _initUI() {

        //--init our health meter--//
        this._initHealthMeter();

        //--create score panel--//
        const scorePanel = P3.createSprite("score_panel", this._uiHolder);
        scorePanel.x = -325;
        scorePanel.y = -55;

        const scoreView = createText("0", {size: 50, fill: 0xffffff, stroke: 0xabc3b0, strokeWidth: 2}, "Digital-7");
        scoreView.position.set(-50, 70);
        scoreView.anchor.set(0);
        scorePanel.addChild(scoreView);

        const multiPanel = P3.createSprite("multiplier_sheet", scorePanel);
        multiPanel.position.set(120, 65);
        multiPanel.anchor.set(0.5, 0);
        scorePanel.multiPanel = multiPanel;

        Common.multiPanelPos = new PIXI.Point(multiPanel.x, multiPanel.y);

        const multiView = createText("1", {size: 31, fill: 0x644727});
        multiView.y = 30;
        multiView.x = -10;
        multiView.anchor.set(0, 0.5);
        multiPanel.addChild(multiView);

        this._scorePanel = scorePanel;
        this._scorePanel.scoreView = scoreView;
        this._scorePanel.multiView = multiView;
        this._scorePanel.back1 = P3.createSprite("score_panel_tube_1", this._uiHolder);
        this._scorePanel.back2 = P3.createSprite("score_panel_tube_2", this._uiHolder);

        this._scorePanel.back1.x = this._scorePanel.x;
        this._scorePanel.back1.y = this._scorePanel.y;
        this._scorePanel.back2.x = this._scorePanel.x;
        this._scorePanel.back2.y = this._scorePanel.y;

        let bulletBack1 = P3.createSprite("ammo_loading_tube_1", this._uiHolder);
        let bulletBack2 = P3.createSprite("ammo_loading_tube_2", this._uiHolder);

        this._bulletPanel = P3.createSprite("ammo_loading_panel", this._uiHolder);
        this._bulletPanel.back1 = bulletBack1;
        this._bulletPanel.back2 = bulletBack2;
        this._bulletPanel.scale = bulletBack1.scale = bulletBack2.scale = new PIXI.Point(0.9, 0.9);

        let reloadText = createText(Common.copy.reload[Common.language], {
            size: 50,
            fill: 0xfb002e,
            stroke: 0x9a0213,
            strokeWidth: 2
        });

        this._uiHolder.addChild(reloadText);

        reloadText.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 + 255;
        reloadText.y = Common.STAGE_HEIGHT * 0.5;

        reloadText.anchor.set(0.5);

        this._bulletPanel.reloadText = reloadText;

        let lLightning = P3.createSprite("reload_warning_001", reloadText);
        let rLightning = P3.createSprite("reload_warning_001", reloadText);
        lLightning.anchor.set(0.5);
        rLightning.anchor.set(0.5);

        lLightning.x = -reloadText.width * 0.5 - 20;
        rLightning.x = reloadText.width * 0.5 + 20;

        reloadText.visible = false;

        reloadText.lLight = lLightning;
        reloadText.rLight = rLightning;

        this._bulletPanel.x = (Common.STAGE_WIDTH - P3.View.width) * 0.5 - 35;
        this._bulletPanel.y = P3.View.height * 0.5 - 35;

        this._bulletPanel.back1.x = this._bulletPanel.x;
        this._bulletPanel.back1.y = this._bulletPanel.y;
        this._bulletPanel.back2.x = this._bulletPanel.x;
        this._bulletPanel.back2.y = this._bulletPanel.y;

        const cb = {
            onUnpause: this._pauseButtonDown.bind(this)
        };

        this._scorePanel.startPos = new PIXI.Point(this._scorePanel.x, this._scorePanel.y);
        this._scorePanel.back1.startPos = new PIXI.Point(this._scorePanel.back1.x, this._scorePanel.back1.y);
        this._scorePanel.back2.startPos = new PIXI.Point(this._scorePanel.back2.x, this._scorePanel.back2.y);

        this._bulletPanel.startPos = new PIXI.Point(this._bulletPanel.x, this._bulletPanel.y);
        this._bulletPanel.back1.startPos = new PIXI.Point(this._bulletPanel.back1.x, this._bulletPanel.back1.y);
        this._bulletPanel.back2.startPos = new PIXI.Point(this._bulletPanel.back2.x, this._bulletPanel.back2.y);

        //--create pause panel--//
        this._pausePanel = new PausePanel(this._uiHolder, cb);
        this._pausePanel.x = Common.STAGE_WIDTH * 0.5;
        this._pausePanel.visible = false;

        //--create mute btn--//
        this._soundBtn = P3.simpleMute();
        this._soundBtn.y = 100;

        //--create pause btn--//
        this._pauseBtn = P3.simpleButton("small", "pause", this._pauseButtonDown.bind(this), this);
        this._pauseBtn.y = 100;

        this._homeBtn = P3.simpleButton("small", "home", this._homeButtonDown.bind(this), this);
        this._homeBtn.x = -P3.View.width * 0.5 + 75;
        this._homeBtn.y = 100;
        this._homeBtn.visible = false;

        this._uiHolder.addChild(this._pauseBtn, this._soundBtn, this._homeBtn);

    }

    /**
     * @private
     */
    _initHealthMeter() {
        const barBg = P3.createSprite("life_meter_bg", this._uiHolder);
        barBg.y = P3.View.height - barBg.height;

        const barFill = P3.createSprite("life_meter_fill", barBg);
        barFill.position.set(-barFill.width * 0.5 + 50, 8);
        barFill.anchor.set(0);
        Common.barMaxFill = barFill.width;

        const fillParticles = P3.createParticles(barFill, "part_life_meter", {name: "bubble_0", count: 1});
        fillParticles.updateOwnerPos(15, 15);
        this._particles.push(fillParticles);

        const mask = new PIXI.Graphics();
        barBg.addChild(mask);
        mask.drawRect(0, 0, barFill.width, barFill.height);
        mask.position = barFill.position;
        mask.y += 6;
        barFill.mask = mask;
        mask.barEnd = P3.createSprite("life_meter_fill_end", barBg);
        mask.barEnd.x = Common.barMaxFill - 195;
        mask.barEnd.y = 13;

        this._meter = mask;
        this._meter.panel = barBg;
        this._meter.panel.x = Common.STAGE_WIDTH * 0.5;

        this._meter.panel.startPos = {x: this._meter.panel.x, y: this._meter.panel.y};
    }

    _createRicochet(pos) {
        const emitter = P3.createParticles(this._worldHolder, "part_missed_shot", {
            name: "part_missedshot_",
            count: 1
        }, null, true);
        emitter.updateOwnerPos(pos.x, pos.y);
        //--emit and cleanup--//
        emitter.playOnce(()=> {
            this._worldHolder.removeChild(emitter);
            emitter.destroy();
        });
    }

    /**
     * @deprecated
     * @param pos
     * @private
     */
    _createHit(pos) {
        const assets = Common.assets;
        const textures = assets.texture("part_hit_shot_1");
        const config = assets.json("part_hit_shot");
        const emitter = new PIXI.particles.Emitter(this._worldHolder, textures, config);
        emitter.updateOwnerPos(pos.x, pos.y);
        emitter.playOnce(()=> {
            this._worldHolder.removeChild(emitter);
            emitter.destroy();
        });
    }


    /**
     * @param pause {boolean}
     * @private
     */
    _pauseProjectiles(pause) {
        const len = this._pathBetties.length;
        for (let i = 0; i < len; i++) {
            this._pathBetties[i].pauseProjectiles(pause);
        }
    }

    /**
     * Fired when user input down on bg (or otherwise doesn't input down on an enemy, so a miss)
     * @param event {event}
     * @private
     */
    _missShot(event) {
        if (this._running && this._scoreManager.gotBullets()) {

            this._scoreManager.useBullet();
            this._scoreManager.resetMultiplier();

            Common.audio.playSound([
                "sfx_firelaser1",
                "sfx_firelaser2",
                "sfx_firelaser3"
            ]);

            const pos = event.data.getLocalPosition(this._worldHolder);
            this._createRicochet(pos);
        }
    }

    /**
     * @deprecated
     * @param event {event}
     * @private
     */
    _muteButtonDown(event) {
        Common.isMute = !Common.isMute;
        Common.audio.mute(Common.isMute);
        Common.saveMute();
        if (!Common.isMute) this._soundBtn.icon.texture = Common.assets.texture("icon_sound_on");
        else this._soundBtn.icon.texture = Common.assets.texture("icon_sound_off");
    }

    /**
     * @param event {event}
     * @private
     */
    _pauseButtonDown(event) {

        if (this._inGrace || this.panelAnimating || this._gameend) return;

        if (!Common.paused) {

            this.isPause = true;

            TweenMax.pauseAll();
            //let root = TimelineLite.exportRoot();
            //root.timeScale(0);

            this._pausePanel.bounceIn();
            this._worldHolder.filters = [this._blurFilter];

            //is paused
            this.panelAnimating = true;
            TweenMax.to(this._meter.panel, 0.2, {y: this._meter.panel.y + 170});

            TweenMax.to(this._scorePanel, 0.2, {y: this._scorePanel.y - 170});
            TweenMax.to(this._scorePanel.back1, 0.2, {y: this._scorePanel.y - 170});
            TweenMax.to(this._scorePanel.back2, 0.2, {y: this._scorePanel.y - 170});

            TweenMax.to(this._bulletPanel, 0.2, {x: this._bulletPanel.x - 270});
            TweenMax.to(this._bulletPanel.back1, 0.2, {x: this._bulletPanel.x - 270});
            TweenMax.to(this._bulletPanel.back2, 0.2, {
                x: this._bulletPanel.x - 270,
                onComplete: ()=> {
                    this.panelAnimating = false;
                }
            });

            this.afx.stop();
            this.afx = null;
            this.afx = Common.audio.playSound("sfx_ambient_popup_loop_00", {loop: true});

            Common.audio.stopMusic();
            const sound = Common.audio.playSound("music_menu_loop_start_00");
            sound.howl.on("end", ()=> {
                Common.audio.stopMusic();
                Common.audio.playMusic("music_menu_loop_00");
            });


        } else {

            this.isPause = false;

            TweenMax.resumeAll();
            //let root = TimelineLite.exportRoot();
            //root.timeScale(1);

            this._worldHolder.filters = null;
            this.panelAnimating = true;

            TweenMax.to(this._meter.panel, 0.2, {y: this._meter.panel.startPos.y});

            TweenMax.to(this._scorePanel, 0.2, {y: this._scorePanel.startPos.y});
            TweenMax.to(this._scorePanel.back1, 0.2, {y: this._scorePanel.back1.startPos.y});
            TweenMax.to(this._scorePanel.back2, 0.2, {y: this._scorePanel.back2.startPos.y});

            TweenMax.to(this._bulletPanel, 0.2, {x: this._bulletPanel.startPos.x});
            TweenMax.to(this._bulletPanel.back1, 0.2, {x: this._bulletPanel.startPos.x});
            TweenMax.to(this._bulletPanel.back2, 0.2, {
                x: this._bulletPanel.startPos.x,
                onComplete: ()=> {
                    this.panelAnimating = false;
                }
            });

            this.afx.stop();
            this.afx = Common.audio.playMusic("sfx_ambient_loop_00", {loop: true});

            Common.audio.stopMusic();
            Common.audio.playMusic("music_gameplay_loop_00");


        }
        delay(()=> {
            this._pauseBtn.visible = !this._pauseBtn.visible;

            this._pausePanel.visible = !this._pausePanel.visible;
            this._homeBtn.visible = !this._homeBtn.visible;

            Common.paused = this._pausePanel.visible;
            this._running = !Common.paused;

            if (!Common.paused) {
                if (this._onUnpauseStartTutorial) {
                    console.log("starting tut");
                    this._inGrace = true;
                    this._running = false;
                    this.tutorial();
                    this._onUnpauseStartTutorial = false;
                }
            }
        }, 0.05, this);

        this._bettyController.pausePeekTweens(Common.paused);
        this._pauseProjectiles(Common.paused);
    }

    _homeButtonDown(event) {
        this.signals.home.dispatch(this);
    }

    _onBulletsEmpty() {
        this.cursor = "url(./assets/images/icon_target_reload.png) 25 25, auto";
    }

    _onBulletsReload() {
        this.cursor = "url(./assets/images/icon_target.png) 25 25, auto";
    }
}

/**
 * rounds x to tile width
 * @param sprite
 */
function roundToTileGrid(sprite) {
    const tWid = 300;
    sprite.x = Math.round(sprite.x / tWid) * tWid;
    sprite.y = Math.round(sprite.y);
}